import { faBarsFilter } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge } from "components/badge/Badge";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Drawer from "components/drawerSlide/drawer";
import Checkbox from "components/form/checkbox/Checkbox";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import { useAppSelector } from "hooks/redux";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type TRainCheckStatus = "valid" | "redeemed" | "expired";

export interface IRaincheckFilters {
    offset: number;
    search: string;
    start_date: Date;
    end_date: Date;
    min_balance: string;
    max_balance: string;
    status: Array<TRainCheckStatus>;
}

interface IProps {
    open: boolean;
    filters: IRaincheckFilters;
    onClose: () => void;
    onOk: (updatedSelection: IRaincheckFilters) => void;
}

export default function RainCheckFilterDrawer(props: IProps) {
    const { open, filters, onClose, onOk } = props;
    const { t } = useTranslation();
    const { facilityStore } = useAppSelector(store => store);
    const { getCurrencySymbol } = useCurrencyFacility();

    const [state, setState] = useState<IRaincheckFilters>({
        offset: 0,
        search: "",
        start_date: null,
        end_date: null,
        min_balance: "",
        max_balance: "",
        status: null,
    });

    useEffect(() => {
        if (open && filters) {
            setState({ ...filters });
        }
    }, [open, filters]);

    function updateStatusSelection(status: TRainCheckStatus) {
        let updatedSelection = state?.status ? [...state.status] : [];
        if (updatedSelection?.includes(status)) {
            updatedSelection = updatedSelection?.filter(s => s !== status);
        } else {
            updatedSelection.push(status);
        }
        setState(prev => ({ ...prev, status: updatedSelection }));
    }

    function clearSelection() {
        setState(prev => ({
            ...prev,
            // offset: 0,
            // search: "",
            start_date: null,
            end_date: null,
            min_balance: "",
            max_balance: "",
            status: null,
        }));
    }

    function handleOk() {
        void onOk({ ...state, status: state?.status?.length === 0 ? null : state?.status });
        void onClose();
    }

    return (
        <Drawer
            open={open}
            headerLeading={
                <span className="deposit-filters-drawer-icon" aria-hidden="true">
                    <FontAwesomeIcon icon={faBarsFilter} />
                </span>}
            title={t("Filter rain checks by")}
            subtitle={t("Apply filters to table data")}
            onClose={onClose}
            cancelText={t("Cancel")}
            cancelButtonType="text"
            showCancel
            backDropClose
            secondaryActions={[{ label: t("Clear all"), action: () => clearSelection(), buttonType: "secondary" }]}
            onOk={handleOk}
            okText={t("Apply")}
            okDisabled={(state?.start_date && state?.end_date && state?.start_date > state?.end_date) || (state?.end_date && !state?.start_date)}
        >
            <Drawer.Section bordered>
                <FormLayout>
                    <FormLayout.Group>
                        <DatePickerInput
                            months={1}
                            position="left"
                            startingDate={state?.start_date}
                            setStartingDate={(start_date: Date) => setState(prev => ({ ...prev, start_date: start_date }))}
                            label={t("Start date")}
                            allowEmptySingleDate
                            allowClear
                            error={(state?.end_date && !state?.start_date)}
                            helpText={(state?.end_date && !state?.start_date) ? t("Start date required") : undefined}
                        />
                    </FormLayout.Group>
                    <FormLayout.Group>
                        <DatePickerInput
                            months={1}
                            position="left"
                            startingDate={state?.end_date}
                            setStartingDate={(end_date: Date) => setState(prev => ({ ...prev, end_date: end_date }))}
                            label={t("End date")}
                            allowEmptySingleDate
                            allowClear
                            error={state?.start_date && state?.end_date && state?.start_date > state?.end_date}
                            helpText={(state?.start_date && state?.end_date && state?.start_date > state?.end_date) ? t("End date must be after start date") : undefined}
                        />
                    </FormLayout.Group>
                </FormLayout>
            </Drawer.Section>
            <Drawer.Section bordered>
                <p className="deposit-filters-drawer-heading">{t("Balance")}</p>
                <FormLayout>
                    <FormLayout.Group>
                        <Input
                            label={t("Min.")}
                            value={state?.min_balance ?? ""}
                            onChange={e => setState(prev => ({ ...prev, min_balance: e.target.value }))}
                            type="number"
                            prefix={getCurrencySymbol(facilityStore?.facility?.default_currency_code) ?? "$"}
                            placeholder={"10"} />
                        <Input
                            label={t("Max.")}
                            value={state?.max_balance ?? ""}
                            onChange={e => setState(prev => ({ ...prev, max_balance: e.target.value }))}
                            type="number"
                            prefix={getCurrencySymbol(facilityStore?.facility?.default_currency_code) ?? "$"}
                            placeholder={"25"} />
                    </FormLayout.Group>
                </FormLayout>
            </Drawer.Section>
            <Drawer.Section bordered>
                <p className="deposit-filters-drawer-heading">{t("Status")}</p>
                <FormLayout>
                    <FormLayout.Group>
                        <Checkbox
                            checked={state?.status?.includes("valid")}
                            labelHidden
                            size="small"
                            onChange={() => updateStatusSelection("valid")}
                            label={<Badge type="success" outline size="small">{t("Valid")}</Badge>} />
                    </FormLayout.Group>
                    <FormLayout.Group>
                        <Checkbox
                            checked={state?.status?.includes("expired")}
                            labelHidden
                            size="small"
                            onChange={() => updateStatusSelection("expired")}
                            label={<Badge type="gray" outline size="small">{t("Expired")}</Badge>} />
                    </FormLayout.Group>
                    <FormLayout.Group>
                        <Checkbox
                            checked={state?.status?.includes("redeemed")}
                            labelHidden
                            size="small" onChange={() => updateStatusSelection("redeemed")}
                            label={<Badge type="warning" outline size="small">{t("Redeemed")}</Badge>} />
                    </FormLayout.Group>
                </FormLayout>
            </Drawer.Section>
        </Drawer>
    );
}